import { first } from 'rxjs/operators';

import { StatusCode } from '@swf/core/enums/statusCode';
import type { RequestError } from '@swf/core/errors/RequestError';
import { getState, select } from '@swf/core/store';
import type { EffectParams } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { RaceType } from '@swf/core/types/CasinoRaces';

import { checkAuth } from './utils';

const race = async (
  payload: {
    raceId: string;
    raceTypes: RaceType[];
    tncVersion: number;
    game?: CasinoGame;
    join?: boolean;
    launch?: boolean;
    externalLaunch?: boolean;
    widgetId?: string;
  },
  { actions, dispatch, rootState, request }: EffectParams
) => {
  const authSubject = checkAuth({ dispatch, rootState, actions });
  const { externalLaunch, game, raceId, raceTypes, tncVersion } = payload;
  const isLiveRace = raceTypes?.includes(RaceType.LDCNT);
  const raceTermsEnabled = select.casino.raceTermsAcceptanceRequired(rootState, isLiveRace);

  let showTerms = false;
  if (!authSubject) {
    await dispatch.casino.fetchAppData();
    const state = getState();
    showTerms = raceTermsEnabled && !select.casino.acceptedRacesTermsAreCurrent(state, tncVersion);
  }

  if (authSubject) {
    return authSubject.subscribe(() => {
      dispatch.casino.race({ ...payload });
    });
  }

  if (showTerms) {
    dispatch.modal.enqueue({
      path: '/casino/help',
      locationState: { acceptanceRequired: true, version: tncVersion }
    });
    actions
      .pipe(first(({ state }) => select.casino.acceptedRacesTermsAreCurrent(state, tncVersion)))
      .subscribe(() => {
        if (externalLaunch && game) {
          dispatch.modal.enqueue({ path: '/casino/game-info', locationState: { game, raceId } });
        } else {
          dispatch.casino.race(payload);
        }
      });
    return;
  }

  if (payload.join) {
    try {
      await request({
        service: 'casino.races.join',
        body: { raceId: payload.raceId }
      });
    } catch (err) {
      const { headers, body } = err as RequestError;
      const statusCode = headers?.statusCode;
      switch (statusCode) {
        case StatusCode.RACE_PROMO_BAN:
          dispatch.modal.enqueue({
            path: '/casino/race-promo-ban',
            locationState: { ...body, statusCode }
          });
          break;
        default:
          dispatch.modal.enqueue({
            path: '/casino/error',
            locationState: { ...body, statusCode }
          });
      }
    }
  }

  if (payload.game && (!payload.join || (payload.join && payload.launch))) {
    if (externalLaunch && game) {
      dispatch.modal.enqueue({ path: '/casino/game-info', locationState: { game, raceId } });
    } else {
      await dispatch.casino.launch(payload);
    }
  }
};

export default race;
